import { DicomMetadataStore } from '../services/DicomMetadataStore/DicomMetadataStore';

function parseUIDsFromImageId(imageId) {
  if (!imageId || typeof imageId !== 'string') {
    return {};
  }

  const studyMatch = imageId.match(/\/studies\/([^/]+)/i);
  const seriesMatch = imageId.match(/\/series\/([^/]+)/i);
  const instanceMatch = imageId.match(/\/instances\/([^/]+)/i);

  return {
    StudyInstanceUID: studyMatch?.[1],
    SeriesInstanceUID: seriesMatch?.[1],
    SOPInstanceUID: instanceMatch?.[1],
  };
}

function normalizeUID(value) {
  if (value === null || value === undefined) {
    return undefined;
  }

  const trimmed = `${value}`.trim();
  if (!trimmed || trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null') {
    return undefined;
  }

  return trimmed;
}

function getSessionIdFromCookieOrStorage() {
  const cookieMatch = /(?:^|; )session_id=([^;]*)/.exec(document.cookie);
  const cookieSessionId = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]).trim() : '';

  if (cookieSessionId && cookieSessionId !== 'undefined' && cookieSessionId !== 'null') {
    return cookieSessionId;
  }

  const storageSessionId = localStorage.getItem('session_id');
  if (storageSessionId && storageSessionId !== 'undefined' && storageSessionId !== 'null') {
    return storageSessionId;
  }

  return null;
}

function resolveReferencedInstance({
  seriesMetadata,
  referencedImageId,
  sopInstanceUID,
  referenceStudyUID,
  referenceSeriesUID,
}) {
  const normalizedReferencedImageId = referencedImageId ? `${referencedImageId}`.trim() : undefined;
  const normalizedSOPInstanceUID = normalizeUID(sopInstanceUID);

  const seriesInstances = Array.isArray(seriesMetadata?.instances) ? seriesMetadata.instances : [];

  const instanceBySeriesImageId = normalizedReferencedImageId
    ? seriesInstances.find(instance => instance?.imageId === normalizedReferencedImageId)
    : undefined;

  const instanceBySeriesSOP = normalizedSOPInstanceUID
    ? seriesInstances.find(
        instance => normalizeUID(instance?.SOPInstanceUID) === normalizedSOPInstanceUID
      )
    : undefined;

  const instanceByStoreImageId = normalizedReferencedImageId
    ? DicomMetadataStore.getInstanceByImageId(normalizedReferencedImageId)
    : undefined;

  const fallbackInstance =
    instanceBySeriesImageId || instanceBySeriesSOP || instanceByStoreImageId || seriesInstances[0];

  return {
    StudyInstanceUID: normalizeUID(
      fallbackInstance?.StudyInstanceUID || seriesMetadata?.StudyInstanceUID || referenceStudyUID
    ),
    SeriesInstanceUID: normalizeUID(
      fallbackInstance?.SeriesInstanceUID || seriesMetadata?.SeriesInstanceUID || referenceSeriesUID
    ),
    SOPInstanceUID: normalizeUID(fallbackInstance?.SOPInstanceUID),
    FrameOfReferenceUID: normalizeUID(fallbackInstance?.FrameOfReferenceUID),
  };
}

function getProjectionAxesFromNormal(viewPlaneNormal) {
  const normalArray = Array.isArray(viewPlaneNormal)
    ? viewPlaneNormal
    : [viewPlaneNormal?.[0] ?? 0, viewPlaneNormal?.[1] ?? 0, viewPlaneNormal?.[2] ?? 1];
  const absNormal = normalArray.map(value => Math.abs(Number(value) || 0));
  const dominantAxis = absNormal.indexOf(Math.max(...absNormal));

  if (dominantAxis === 0) {
    return [1, 2];
  }

  if (dominantAxis === 1) {
    return [0, 2];
  }

  return [0, 1];
}

function computeAreaFromPolyline(points, viewPlaneNormal) {
  if (!Array.isArray(points) || points.length < 3) {
    return null;
  }

  const [axisA, axisB] = getProjectionAxesFromNormal(viewPlaneNormal);
  let area2 = 0;

  for (let i = 0; i < points.length; i++) {
    const current = points[i] || [];
    const next = points[(i + 1) % points.length] || [];
    const x1 = Number(current[axisA]) || 0;
    const y1 = Number(current[axisB]) || 0;
    const x2 = Number(next[axisA]) || 0;
    const y2 = Number(next[axisB]) || 0;
    area2 += x1 * y2 - x2 * y1;
  }

  return Math.abs(area2) / 2;
}

function buildFallbackTextBox(points) {
  const safePoint = Array.isArray(points?.[0]) ? points[0] : [0, 0, 0];

  return {
    hasMoved: false,
    worldPosition: [safePoint[0] || 0, safePoint[1] || 0, safePoint[2] || 0],
  };
}

function distance3D(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return 0;
  }

  const dx = (Number(a[0]) || 0) - (Number(b[0]) || 0);
  const dy = (Number(a[1]) || 0) - (Number(b[1]) || 0);
  const dz = (Number(a[2]) || 0) - (Number(b[2]) || 0);

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function validateMeasurementPayload(measurementObj) {
  const requiredFields = [
    ['SOPInstanceUID', measurementObj?.SOPInstanceUID],
    ['SeriesInstanceUID', measurementObj?.SeriesInstanceUID],
    ['StudyInstanceUID', measurementObj?.StudyInstanceUID],
  ];

  const missingFields = requiredFields
    .filter(([, value]) => !normalizeUID(value))
    .map(([field]) => field);

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

export default function addToVIDAADB(measurementData, measurementService, uiNotificationService) {
  if (measurementData.length === 0) {
    return;
  }

  const hasUnclosedSpline = measurementData.some(measurement => {
    if (measurement?.toolName !== 'SplineROI') {
      return false;
    }

    const contourClosed =
      measurement?.contour?.closed ??
      measurement?.annotationData?.contour?.closed ??
      measurement?.data?.contour?.closed;

    if (contourClosed === false) {
      return true;
    }

    const handlesPoints =
      measurement?.annotationData?.handles?.points ||
      measurement?.points ||
      measurement?.contour?.polyline ||
      [];
    return Array.isArray(handlesPoints) && handlesPoints.length > 0 && contourClosed !== true;
  });

  if (hasUnclosedSpline) {
    uiNotificationService?.show?.({
      title: 'Save blocked',
      message:
        'The measurements have not been saved because some splines are not closed. Please close the curve before saving.',
      type: 'error',
      duration: 6000,
    });
    return;
  }

  console.log('Measurements to save:', measurementData);

  let promises = [];
  const skippedMeasurements = [];
  measurementData.forEach(measurement => {
    const { referenceStudyUID, referenceSeriesUID, getReport, uid, toolName } = measurement;

    const seriesMetadata = DicomMetadataStore.getSeries(referenceStudyUID, referenceSeriesUID);
    const commonRowItems = _getCommonRowItems(measurement, seriesMetadata);
    const label = measurement.label ? measurement.label : 'custom';

    let measurementGeometry = {};
    let measurementMetrics = {};
    let persistedSplinePoints = [];
    let persistedSplineClosed = true;

    // 1. Detect if the tool is a Spline
    const isSpline = toolName === 'SplineROI';

    console.log(`Processing measurement UID: ${uid}, Tool: ${toolName}, Is Spline: ${isSpline}`);

    if (isSpline) {
      console.log('Extracting geometry for Spline tool:', measurement);
      const annotationData = measurement.annotationData || measurement.data || {};
      const nativePoints =
        annotationData?.handles?.points ||
        annotationData?.handles?.active ||
        measurement.points ||
        [];
      const isClosed = measurement.contour?.closed ?? annotationData?.contour?.closed ?? true;

      // Extract the dense array of points forming the rendered polyline curve
      const polylineNativePoints =
        annotationData?.contour?.polyline || measurement.contour?.polyline || [];
      persistedSplinePoints = polylineNativePoints;
      persistedSplineClosed = isClosed;

      measurementGeometry = {
        mode: 'spline',
        geometry: {
          // Save only control points; polyline is re-interpolated at render time.
          controlPoints: nativePoints.map(p => ({ x: p[0], y: p[1], z: p[2] })),
          closed: isClosed,
        },
        // Added dense coordinates array to match backend expectations exactly
        polylinePoints: polylineNativePoints,
      };

      const firstPoint = polylineNativePoints[0] || nativePoints[0] || [0, 0, 0];
      const secondPoint = polylineNativePoints[1] || nativePoints[1] || firstPoint;
      let totalLength = 0;
      if (Array.isArray(polylineNativePoints) && polylineNativePoints.length > 1) {
        for (let i = 1; i < polylineNativePoints.length; i++) {
          totalLength += distance3D(polylineNativePoints[i - 1], polylineNativePoints[i]);
        }
      } else {
        totalLength = distance3D(firstPoint, secondPoint);
      }

      measurementGeometry.line = {
        start: {
          x: Number(firstPoint[0]) || 0,
          y: Number(firstPoint[1]) || 0,
          z: Number(firstPoint[2]) || 0,
        },
        end: {
          x: Number(secondPoint[0]) || 0,
          y: Number(secondPoint[1]) || 0,
          z: Number(secondPoint[2]) || 0,
        },
      };
      measurementGeometry.length = {
        value: Number(totalLength) || 0,
        units: 'mm',
      };
    } else {
      // Original mapping structure for linear tools (e.g., Length)
      measurementGeometry = {
        mode: 'polyline',
        line: {
          start: {
            x: measurement.points[0][0],
            y: measurement.points[0][1],
            z: measurement.points[0][2],
          },
          end: {
            x: measurement.points[1][0],
            y: measurement.points[1][1],
            z: measurement.points[1][2],
          },
        },
        // Fallback empty array or mapped points can be passed if non-splines require this field on your API
        polylinePoints: [],
      };
    }

    // 2. Safely extract metrics (Area calculation for Spline)
    if (isSpline) {
      // Try to read cachedStats from the root object (as structured in your JSON) or from annotationData as fallback
      const cachedStats =
        measurement.cachedStats ||
        measurement.annotationData?.cachedStats ||
        measurement.data?.cachedStats ||
        (measurement.data && !Array.isArray(measurement.data) ? measurement.data : {}) ||
        {};

      let areaValue = null;
      let areaUnit = 'mm²'; // Default unit for areas in Cornerstone

      // Iterate over dynamic target keys (e.g., "volumeId:cornerstoneStreamingImageVolume...")
      const targetKeys = Object.keys(cachedStats);

      if (targetKeys.length > 0) {
        // Get the first computed statistical dataset for the volume/image target
        const statsForTarget = cachedStats[targetKeys[0]];

        if (statsForTarget && typeof statsForTarget === 'object') {
          // Map properties matching the exact casing from your JSON payload ('area' and 'areaUnit')
          areaValue = statsForTarget.area ?? statsForTarget.Area ?? null;
          areaUnit = statsForTarget.areaUnit ?? statsForTarget.AreaUnit ?? 'mm²';
        }
      }

      // Fallback safe mechanism in case the MeasurementService flattened the measurement properties elsewhere
      if (areaValue === null) {
        areaValue = measurement.area?.value ?? measurement.area ?? null;
        if (measurement.area?.units) {
          areaUnit = measurement.area.units;
        }
      }

      if (areaValue === null) {
        const annotationData = measurement.annotationData || measurement.data || {};
        const polylinePoints =
          annotationData?.contour?.polyline ||
          measurement.contour?.polyline ||
          measurement.points ||
          [];
        areaValue = computeAreaFromPolyline(
          polylinePoints,
          measurement.metadata?.viewPlaneNormal || measurement.camera?.viewPlaneNormal
        );
      }

      // Assign to the final metrics object ensuring that numerical values are safely converted for MongoDB
      measurementMetrics = {
        area: {
          value: Number(areaValue ?? 0),
          units: areaUnit,
        },
        cachedStats: cachedStats, // Keep the full original object if persistence in database is required
      };
    } else {
      // Original metrics extraction for linear/length tools
      if (getReport) {
        const report = getReport(measurement);
        measurementMetrics = {
          length: {
            value: report.values[report.columns.indexOf('Length')],
            units: report.values[report.columns.indexOf('Unit')],
          },
        };
      }
    }

    // 3. Assemble unified object structure for MongoDB
    const frameOfReferenceUID =
      measurement.FrameOfReferenceUID || measurement.metadata?.FrameOfReferenceUID;
    const viewPlaneNormal = measurement.metadata?.viewPlaneNormal || [0, 0, -1];
    const viewUp = measurement.metadata?.viewUp || [0, -1, 0];
    const referencedImageId = measurement.metadata?.referencedImageId;
    const parsedUIDs = parseUIDsFromImageId(referencedImageId);
    const sopInstanceUID =
      measurement.annotationSOPInstanceUID ||
      measurement.SOPInstanceUID ||
      measurement.metadata?.SOPInstanceUID ||
      parsedUIDs.SOPInstanceUID;
    const metadataFallbacks = resolveReferencedInstance({
      seriesMetadata,
      referencedImageId,
      sopInstanceUID,
      referenceStudyUID,
      referenceSeriesUID,
    });
    const normalizedSeriesInstanceUID =
      measurement.metadata?.SeriesInstanceUID ||
      referenceSeriesUID ||
      parsedUIDs.SeriesInstanceUID ||
      metadataFallbacks.SeriesInstanceUID;
    const normalizedStudyInstanceUID =
      measurement.metadata?.StudyInstanceUID ||
      referenceStudyUID ||
      parsedUIDs.StudyInstanceUID ||
      metadataFallbacks.StudyInstanceUID;

    let measurementObj = {
      id: measurement.uid,
      patient: commonRowItems ? commonRowItems['Patient ID'] : undefined,
      type: isSpline ? `${label}-spline` : `${label}-polyline`,
      toolName: toolName,

      ...measurementGeometry, // Spreads mode, geometry, and polylinePoints into the object
      ...measurementMetrics,

      FrameOfReferenceUID: normalizeUID(
        frameOfReferenceUID || metadataFallbacks.FrameOfReferenceUID
      ),
      SOPInstanceUID: normalizeUID(sopInstanceUID || metadataFallbacks.SOPInstanceUID),
      camera: {
        viewPlaneNormal,
        viewUp,
      },

      normalVector: {
        x: viewPlaneNormal?.[0] ?? 0,
        y: viewPlaneNormal?.[1] ?? 0,
        z: viewPlaneNormal?.[2] ?? -1,
      },

      metadata: measurement.metadata,
      textBox:
        measurement.textBox ||
        measurement.annotationData?.handles?.textBox ||
        measurement.data?.handles?.textBox ||
        buildFallbackTextBox(
          measurement.points ||
            measurement.contour?.polyline ||
            measurement.annotationData?.contour?.polyline ||
            []
        ),
      SeriesInstanceUID: normalizeUID(
        seriesMetadata?.SeriesInstanceUID || normalizedSeriesInstanceUID
      ),
      StudyInstanceUID: normalizeUID(
        seriesMetadata?.StudyInstanceUID || normalizedStudyInstanceUID
      ),
      imageModality: seriesMetadata?.Modality,
      ...(isSpline
        ? {
            points: persistedSplinePoints,
            contour: {
              polyline: persistedSplinePoints,
              closed: measurement?.contour?.closed ?? persistedSplineClosed,
            },
          }
        : {}),
    };

    if (measurementObj.metadata) {
      measurementObj.metadata = {
        ...measurementObj.metadata,
        SOPInstanceUID: normalizeUID(measurementObj.metadata.SOPInstanceUID || sopInstanceUID),
        SeriesInstanceUID: normalizeUID(
          measurementObj.metadata.SeriesInstanceUID || normalizedSeriesInstanceUID
        ),
        StudyInstanceUID: normalizeUID(
          measurementObj.metadata.StudyInstanceUID || normalizedStudyInstanceUID
        ),
      };
    }

    const validation = validateMeasurementPayload(measurementObj);
    if (!validation.isValid) {
      skippedMeasurements.push({
        uid: measurement.uid,
        toolName,
        missingFields: validation.missingFields,
        referencedImageId,
        parsedUIDs,
        metadataFallbacks,
      });
      console.error(
        `Skipping measurement ${measurement.uid}: missing required payload fields (${validation.missingFields.join(', ')})`,
        measurementObj
      );
      return;
    }

    console.log('Unified object ready for MongoDB transmission:', measurementObj);

    promises.push(saveMeasurement(measurementObj, isSpline));
  });

  if (skippedMeasurements.length) {
    uiNotificationService?.show?.({
      title: 'Some Measurements Were Skipped',
      message: `Skipped ${skippedMeasurements.length} measurements with missing UIDs. Check console for details.`,
      type: 'error',
      duration: 7000,
    });
  }

  if (!promises.length) {
    return;
  }

  // Server persistence and updating OHIF internal state
  Promise.all(promises)
    .then(responses => {
      return Promise.all(
        responses.map(async response => {
          if (!response.ok) {
            let errorDetails = '';
            try {
              const clonedResponse = response.clone();
              const errorJson = await clonedResponse.json();
              errorDetails = JSON.stringify(errorJson);
            } catch (_jsonError) {
              try {
                errorDetails = await response.text();
              } catch (_textError) {
                errorDetails = '';
              }
            }

            const detailsSuffix = errorDetails ? ` | Details: ${errorDetails}` : '';
            throw new Error(`Error ${response.status}: ${response.statusText}${detailsSuffix}`);
          }
          return response.json();
        })
      );
    })
    .then(data => {
      console.log('Measurements saved successfully:', data);

      measurementData.forEach(measurement => {
        const existingMeasurement = measurementService.getMeasurement(measurement.uid);
        if (!existingMeasurement) {
          return;
        }

        const updatedMeasurement = { ...existingMeasurement, isSaved: true };
        measurementService.update(measurement.uid, updatedMeasurement, false);
      });

      uiNotificationService?.show?.({
        title: 'Measurement Saved',
        message: `${data.length} measurements saved correctly.`,
        type: 'success',
        duration: 5000,
      });
    })
    .catch(error => {
      console.error('There was a problem saving the measurements:', error);
      uiNotificationService?.show?.({
        title: 'Error Saving Measurement',
        message: `There was a problem sending the measurements to the server. ${error}`,
        type: 'error',
        autoClose: false,
      });
    });
}

async function saveMeasurement(measurementData, isSpline) {
  const API_URL_ENV = process.env.API_URL;

  const endpoint = isSpline ? 'image-annotation-spline' : 'image-annotation';
  const API_URL = `${API_URL_ENV}/annotations/${endpoint}`;

  const viewer_token_raw = sessionStorage.getItem('session_viewer');
  const viewer_token =
    viewer_token_raw && viewer_token_raw !== 'undefined' && viewer_token_raw !== 'null'
      ? viewer_token_raw
      : null;
  let myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/json');
  if (viewer_token) {
    myHeaders.append('Authorization', `Bearer ${viewer_token}`);
  }

  const session_id = getSessionIdFromCookieOrStorage();
  if (session_id) {
    myHeaders.append('x-api-key', session_id);
  }

  const options = {
    method: 'POST',
    headers: myHeaders,
    credentials: 'include',
    body: JSON.stringify(measurementData),
  };

  return fetch(API_URL, options);
}

function _getCommonRowItems(measurement, seriesMetadata) {
  const firstInstance = seriesMetadata?.instances?.[0] || {};
  return {
    'Patient ID': firstInstance.PatientID,
    'Patient Name': firstInstance.PatientName?.Alphabetic || '',
    StudyInstanceUID: measurement.referenceStudyUID,
    SeriesInstanceUID: measurement.referenceSeriesUID,
    SOPInstanceUID: measurement.SOPInstanceUID || measurement.metadata?.SOPInstanceUID,
    Label: measurement.label || '',
  };
}
